/**
 * Global Order Store for frontend-v2
 * Implements Milestone M5: R5 Checkout & Order Screen
 * 
 * Manages:
 * - activeOrder: full OrderDetail for current checkout/order tracking page
 * - orderHistory: array of OrderSummary objects persisted in localStorage('fn_orders')
 * - isCreating, isFetching, isUploadingReceipt, uploadProgress, orderError
 * - Dual persistence with legacy localStorage('fn_last_order')
 * - Automatic cart clearing upon successful order creation
 * - Cross-tab synchronization via window 'storage' listener
 */

import { create } from 'zustand';
import {
  OrderDetail,
  OrderSummary,
  CreateOrderPayload,
  OrderRequestOptions,
  UploadReceiptOptions,
  UploadReceiptResponse,
  OrderStatus,
  createOrder as apiCreateOrder,
  getOrder as apiGetOrder,
  uploadReceipt as apiUploadReceipt,
  cancelOrder as apiCancelOrder,
  cleanOrderCode,
} from '../services/orderApi';
import { useCartStore } from './useCartStore';

export const ORDER_HISTORY_STORAGE_KEY = 'fn_orders';
export const LAST_ORDER_STORAGE_KEY = 'fn_last_order';

export interface OrderState {
  // State
  activeOrder: OrderDetail | null;
  orderHistory: OrderSummary[];
  isCreating: boolean;
  isFetching: boolean;
  isUploadingReceipt: boolean;
  uploadProgress: number;
  orderError: string | null;

  // Actions
  createOrder: (payload: CreateOrderPayload, options?: OrderRequestOptions) => Promise<OrderDetail>;
  fetchOrder: (orderCode: string, options?: OrderRequestOptions) => Promise<OrderDetail>;
  uploadReceipt: (orderCode: string, file: File | Blob, options?: UploadReceiptOptions) => Promise<UploadReceiptResponse>;
  cancelOrder: (orderCode: string, options?: OrderRequestOptions) => Promise<boolean>;
  clearActiveOrder: () => void;
  setOrderHistory: (history: OrderSummary[]) => void;
  loadStoredHistory: () => void;
  clearOrderError: () => void;
  getOrderFromHistory: (orderCode: string) => OrderSummary | undefined;
}

/**
 * Transforms a full OrderDetail into an OrderSummary for history storage.
 */
export function toOrderSummary(detail: OrderDetail): OrderSummary {
  return {
    order_code: detail.order_code,
    customer_username: detail.customer_username,
    total_vbucks: detail.total_vbucks,
    total_price: detail.total_price,
    currency: detail.currency,
    payment_method: detail.payment_method,
    status: detail.status,
    created_at: detail.created_at,
    expires_at: detail.expires_at,
    items_count: detail.items ? detail.items.length : 0,
    receipt_url: detail.receipt_url || null,
  };
}

/**
 * Persists order history to localStorage with defensive error handling.
 */
export function persistOrderHistory(history: OrderSummary[]): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(ORDER_HISTORY_STORAGE_KEY, JSON.stringify(history));
    }
  } catch {
    // Storage quota or restriction fallback
  }
}

/**
 * Safely reads and sanitizes stored order history from localStorage.
 */
export function readStoredOrderHistory(): OrderSummary[] {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem(ORDER_HISTORY_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed.filter(
            (o) => o && typeof o === 'object' && typeof o.order_code === 'string' && o.order_code.length > 0
          );
        }
      }

      // Fallback: check legacy fn_last_order if fn_orders is empty
      const legacyLast = window.localStorage.getItem(LAST_ORDER_STORAGE_KEY);
      if (legacyLast) {
        const parsedLegacy = JSON.parse(legacyLast);
        if (parsedLegacy?.order_code) {
          return [
            {
              order_code: parsedLegacy.order_code,
              customer_username: '',
              total_vbucks: 0,
              total_price: 0,
              currency: 'ARS',
              payment_method: 'TRANSFERENCIA_BANCARIA',
              status: 'PENDIENTE_PAGO',
              created_at: new Date(parsedLegacy.created_at || Date.now()).toISOString(),
              items_count: 1,
            },
          ];
        }
      }
    }
  } catch {
    // Parsing error
  }
  return [];
}

/**
 * Persists the latest active order reference for legacy cross-compatibility.
 */
export function persistLastOrderReference(orderCode: string): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(
        LAST_ORDER_STORAGE_KEY,
        JSON.stringify({
          order_code: orderCode,
          created_at: Date.now(),
        })
      );
    }
  } catch {}
}

export const useOrderStore = create<OrderState>((set, get) => {
  // Cross-tab synchronization listener
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === ORDER_HISTORY_STORAGE_KEY) {
        const updated = readStoredOrderHistory();
        set({ orderHistory: updated });
      }
    });
  }

  return {
    activeOrder: null,
    orderHistory: readStoredOrderHistory(),
    isCreating: false,
    isFetching: false,
    isUploadingReceipt: false,
    uploadProgress: 0,
    orderError: null,

    createOrder: async (payload: CreateOrderPayload, options?: OrderRequestOptions) => {
      set({ isCreating: true, orderError: null });

      try {
        const order = await apiCreateOrder(payload, options);
        const summary = toOrderSummary(order);

        // Update history: avoid duplicate codes, prepend new order
        const currentHistory = get().orderHistory;
        const filteredHistory = currentHistory.filter((o) => o.order_code !== order.order_code);
        const newHistory = [summary, ...filteredHistory];

        persistOrderHistory(newHistory);
        persistLastOrderReference(order.order_code);

        // Clear shopping cart and applied coupon upon successful order placement
        try {
          useCartStore.getState().clearCart();
          useCartStore.getState().removeCoupon();
        } catch {
          // Non-blocking in headless tests if cart store is uninitialized
        }

        set({
          activeOrder: order,
          orderHistory: newHistory,
          isCreating: false,
          orderError: null,
        });

        return order;
      } catch (err: any) {
        const message = err?.message || 'Error al generar el pedido.';
        set({
          isCreating: false,
          orderError: message,
        });
        throw err;
      }
    },

    fetchOrder: async (orderCode: string, options?: OrderRequestOptions) => {
      const cleanCode = cleanOrderCode(orderCode);
      set({ isFetching: true, orderError: null });

      try {
        const order = await apiGetOrder(cleanCode, options);

        // Update order status in orderHistory if present
        const currentHistory = get().orderHistory;
        const exists = currentHistory.some((o) => o.order_code === order.order_code);
        let updatedHistory = currentHistory;

        if (exists) {
          updatedHistory = currentHistory.map((o) =>
            o.order_code === order.order_code ? { ...o, status: order.status, receipt_url: order.receipt_url } : o
          );
        } else {
          updatedHistory = [toOrderSummary(order), ...currentHistory];
        }

        persistOrderHistory(updatedHistory);
        persistLastOrderReference(order.order_code);

        set({
          activeOrder: order,
          orderHistory: updatedHistory,
          isFetching: false,
          orderError: null,
        });

        return order;
      } catch (err: any) {
        const message = err?.message || 'Error al obtener el pedido.';
        set({
          isFetching: false,
          orderError: message,
        });
        throw err;
      }
    },

    uploadReceipt: async (orderCode: string, file: File | Blob, options?: UploadReceiptOptions) => {
      const cleanCode = cleanOrderCode(orderCode);
      set({ isUploadingReceipt: true, uploadProgress: 0, orderError: null });

      const onProgressProxy = (percent: number) => {
        set({ uploadProgress: percent });
        if (options?.onProgress) options.onProgress(percent);
      };

      try {
        const res = await apiUploadReceipt(cleanCode, file, {
          ...options,
          onProgress: onProgressProxy,
        });

        // Update active order state
        const currentActive = get().activeOrder;
        let updatedActive: OrderDetail | null = currentActive;
        if (currentActive && currentActive.order_code === cleanCode) {
          updatedActive = {
            ...currentActive,
            status: res.status,
            receipt_url: res.receipt_url,
          };
        }

        // Update history entry
        const updatedHistory = get().orderHistory.map((o) =>
          o.order_code === cleanCode ? { ...o, status: res.status, receipt_url: res.receipt_url } : o
        );
        persistOrderHistory(updatedHistory);

        set({
          activeOrder: updatedActive,
          orderHistory: updatedHistory,
          isUploadingReceipt: false,
          uploadProgress: 100,
          orderError: null,
        });

        return res;
      } catch (err: any) {
        const message = err?.message || 'Error al subir comprobante.';
        set({
          isUploadingReceipt: false,
          uploadProgress: 0,
          orderError: message,
        });
        throw err;
      }
    },

    cancelOrder: async (orderCode: string, options?: OrderRequestOptions) => {
      const cleanCode = cleanOrderCode(orderCode);
      set({ orderError: null });

      try {
        const res = await apiCancelOrder(cleanCode, options);

        const currentActive = get().activeOrder;
        let updatedActive = currentActive;
        if (currentActive && currentActive.order_code === cleanCode) {
          updatedActive = {
            ...currentActive,
            status: 'CANCELADO',
            cancelled_by: res.cancelled_by || 'USER',
          };
        }

        const updatedHistory = get().orderHistory.map((o) =>
          o.order_code === cleanCode ? { ...o, status: 'CANCELADO' as OrderStatus } : o
        );
        persistOrderHistory(updatedHistory);

        set({
          activeOrder: updatedActive,
          orderHistory: updatedHistory,
          orderError: null,
        });

        return true;
      } catch (err: any) {
        const message = err?.message || 'Error al cancelar el pedido.';
        set({ orderError: message });
        throw err;
      }
    },

    clearActiveOrder: () => {
      set({ activeOrder: null, orderError: null });
    },

    setOrderHistory: (history: OrderSummary[]) => {
      persistOrderHistory(history);
      set({ orderHistory: history });
    },

    loadStoredHistory: () => {
      const stored = readStoredOrderHistory();
      set({ orderHistory: stored });
    },

    clearOrderError: () => {
      set({ orderError: null });
    },

    getOrderFromHistory: (orderCode: string) => {
      const cleanCode = cleanOrderCode(orderCode);
      return get().orderHistory.find((o) => o.order_code === cleanCode);
    },
  };
});

export default useOrderStore;
